import "@/App.css";
import "./i18n/config";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Navbar, Footer } from "./components/Layout";
import { MachineTranslationNotice } from "./components/MachineTranslationNotice";
import { ChatAssistant } from "./components/ChatAssistant";
import { Toaster } from "./components/ui/sonner";
import { VisitTracker } from "./components/VisitTracker";
import { NewFormationPopup } from "./components/NewFormationPopup";
import { useDeploymentWatcher } from "./hooks/useDeploymentWatcher";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "./i18n/config";

// Pages
import { HomePage } from "./pages/HomePage";
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import { OnlineCoursesPage } from "./pages/OnlineCoursesPage";
import { OnsiteCoursesPage } from "./pages/OnsiteCoursesPage";
import { CourseDetailPage } from "./pages/CourseDetailPage";
import { LearnPage } from "./pages/LearnPage";
import { ApplicationPage } from "./pages/ApplicationPage";
import { MyCoursesPage } from "./pages/MyCoursesPage";
import { AdminDashboard } from "./pages/AdminDashboard";
import { GestionDashboard } from "./pages/GestionDashboard";
import { FormateurDashboard } from "./pages/FormateurDashboard";
import { PaymentSuccessPage, PaymentCancelPage } from "./pages/PaymentPages";
import { AboutPage, ContactPage } from "./pages/StaticPages";
import { MentionsLegalesPage, ConfidentialitePage, CGVPage } from "./pages/LegalPages";
import { FAQPage } from "./pages/FAQPage";
import DevenirApporteurPage from "./pages/DevenirApporteurPage";
import QuoteRequestPage from "./pages/QuoteRequestPage";
import CoPilotagePage from "./pages/CoPilotagePage";
import CertificationPage from "./pages/CertificationPage";
import ApporteurDashboard from "./pages/ApporteurDashboard";
import { BlogPage } from "./pages/BlogPage";
import { BlogPostPage } from "./pages/BlogPostPage";
import { MagicLinkPage } from "./pages/MagicLinkPage";
import { MyDossierPage } from "./pages/MyDossierPage";
import { FinancementsPage } from "./pages/FinancementsPage";
import { ReseauSTSPage } from "./pages/ReseauSTSPage";
import { PlanDuSitePage } from "./pages/PlanDuSitePage";
import { CityLandingPage } from "./pages/CityLandingPage";
import { CityFormationPage } from "./pages/CityFormationPage";
import { ImpactEnvironnementalPage } from "./pages/ImpactEnvironnementalPage";
import CourseContentAdmin from "./pages/CourseContentAdmin";
import CourseLibraryPage from "./pages/CourseLibraryPage";
import CourseReaderPage from "./pages/CourseReaderPage";
import FicheElevePrintPage from "./pages/FicheElevePrintPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const { lang: langParam } = useParams();
  const lang = SUPPORTED_LANGS.includes(langParam) ? langParam : DEFAULT_LANG;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/${lang}/connexion`} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to={`/${lang}/mes-formations`} replace />;
  }

  return children;
};

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Reads the :lang URL prefix and syncs it with i18next. If the prefix is
// not a supported language (e.g. user typed /foo by mistake), strip the bad
// segment and redirect to /{detectedLang}{rest}.
const LangSync = () => {
  const { i18n } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!lang) return;
    if (!SUPPORTED_LANGS.includes(lang)) {
      let stored = DEFAULT_LANG;
      try {
        const ls = localStorage.getItem("i18nextLng");
        if (ls && SUPPORTED_LANGS.includes(ls)) stored = ls;
      } catch (_) {}
      // If the first segment looks like a (bad) 2-letter lang code, strip it;
      // otherwise it's a page path typed without lang prefix — keep it whole.
      const first = location.pathname.split("/")[1] || "";
      const isLangLike = /^[a-z]{2}(-[A-Za-z]{2})?$/.test(first);
      const rest = isLangLike ? (location.pathname.replace(/^\/[^/]+/, "") || "") : location.pathname;
      navigate(`/${stored}${rest}` + location.search + location.hash, { replace: true });
      return;
    }
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
      try { localStorage.setItem("i18nextLng", lang); } catch (_) {}
    }
  }, [lang, i18n, location.pathname, location.search, location.hash, navigate]);

  if (lang && !SUPPORTED_LANGS.includes(lang)) return null;
  return <Outlet />;
};

// Redirect any unprefixed path to /{storedLang}{path}. Prefers explicit
// localStorage choice; falls back to FR (not navigator language) so the
// French homepage is the canonical default for first-time visitors.
const LangRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let stored = DEFAULT_LANG;
    try {
      const ls = localStorage.getItem("i18nextLng");
      if (ls && SUPPORTED_LANGS.includes(ls)) stored = ls;
    } catch (_) {}
    const target = `/${stored}${location.pathname === "/" ? "" : location.pathname}` + location.search + location.hash;
    navigate(target, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

// Layout Component
const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <MachineTranslationNotice />
    </div>
  );
};

// Layout without Footer (for learning pages)
const MinimalLayout = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <MachineTranslationNotice />
    </div>
  );
};

function AppRoutes() {
  useDeploymentWatcher();
  return (
    <>
      <ScrollToTop />
      <VisitTracker />
      <NewFormationPopup />
      <Routes>
        <Route path="/:lang" element={<LangSync />}>
          {/* Public routes with full layout */}
          <Route element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="connexion" element={<LoginPage />} />
            <Route path="inscription" element={<RegisterPage />} />
            <Route path="formations-en-ligne" element={<OnlineCoursesPage />} />
            <Route path="formations-sur-place" element={<OnsiteCoursesPage />} />
            <Route path="formation/:id" element={<CourseDetailPage />} />
            <Route path="a-propos" element={<AboutPage />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="faq" element={<FAQPage />} />
            <Route path="devenir-apporteur" element={<DevenirApporteurPage />} />
            <Route path="devis" element={<QuoteRequestPage />} />
            <Route path="devis-sellerie" element={<QuoteRequestPage />} />
            <Route path="artisan-pres-de-chez-vous" element={<QuoteRequestPage mode="network" />} />
            <Route path="artisan-sts" element={<QuoteRequestPage mode="network" />} />
            <Route path="co-pilotage" element={<CoPilotagePage />} />
            <Route path="certification-entrepreneur-sellerie-garnissage" element={<CertificationPage />} />
            <Route path="copilotage" element={<CoPilotagePage />} />

            {/* Blog */}
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:slug" element={<BlogPostPage />} />

            {/* Magic link + student dossier */}
            <Route path="finaliser-compte/:token" element={<MagicLinkPage />} />
            <Route path="mon-dossier" element={<MyDossierPage />} />

            {/* Financements (SEO) */}
            <Route path="financements" element={<FinancementsPage />} />
            <Route path="reseau-sts" element={<ReseauSTSPage />} />
            <Route path="plan-du-site" element={<PlanDuSitePage />} />
            <Route path="sellier/:slug" element={<CityLandingPage />} />
            <Route path="sellier/:slug/:formation" element={<CityFormationPage />} />
            <Route path="sellerie-et-environnement" element={<ImpactEnvironnementalPage />} />

            {/* Legal pages */}
            <Route path="mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="confidentialite" element={<ConfidentialitePage />} />
            <Route path="cgv" element={<CGVPage />} />

            {/* Payment pages */}
            <Route path="payment/success" element={<PaymentSuccessPage />} />
            <Route path="payment/cancel" element={<PaymentCancelPage />} />

            {/* Protected student routes */}
            <Route path="mes-formations" element={
              <ProtectedRoute>
                <MyCoursesPage />
              </ProtectedRoute>
            } />
            <Route path="candidature/:sessionId" element={<ApplicationPage />} />

            {/* Online course library (logged-in users only) */}
            <Route path="cours-en-ligne" element={
              <ProtectedRoute>
                <CourseLibraryPage />
              </ProtectedRoute>
            } />
            <Route path="cours-en-ligne/:slug" element={
              <ProtectedRoute>
                <CourseReaderPage />
              </ProtectedRoute>
            } />

            {/* Gestionnaire routes */}
            <Route path="gestion" element={
              <ProtectedRoute allowedRoles={['admin', 'gestionnaire']}>
                <GestionDashboard />
              </ProtectedRoute>
            } />

            {/* Formateur routes */}
            <Route path="formateur" element={
              <ProtectedRoute allowedRoles={['admin', 'formateur']}>
                <FormateurDashboard />
              </ProtectedRoute>
            } />

            {/* Apporteur d'affaires routes */}
            <Route path="apporteur" element={
              <ProtectedRoute allowedRoles={['admin', 'apporteur']}>
                <ApporteurDashboard />
              </ProtectedRoute>
            } />
          </Route>

        </Route>

        {/* Learning page with minimal layout (kept Navbar, no Footer). */}
        <Route path="/:lang" element={<LangSync />}>
          <Route element={<MinimalLayout />}>
            <Route path="apprendre/:courseId" element={
              <ProtectedRoute>
                <LearnPage />
              </ProtectedRoute>
            } />
          </Route>
          {/* Bare-layout — no public navbar/footer (admin focused workspace + PDF print). */}
          <Route path="admin" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="admin/cours-en-ligne" element={
            <ProtectedRoute allowedRoles={['admin', 'gestionnaire']}>
              <CourseContentAdmin />
            </ProtectedRoute>
          } />
          <Route path="admin/fiche-eleve/:userId/print" element={
            <ProtectedRoute allowedRoles={['admin', 'gestionnaire']}>
              <FicheElevePrintPage />
            </ProtectedRoute>
          } />
        </Route>

        {/* Anything without a /:lang/ prefix is normalized via redirect. */}
        <Route path="*" element={<LangRedirect />} />
      </Routes>
      <ChatAssistant />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
